import { Link } from "react-router-dom";
import { CheckCircle2, ChevronLeft, MapPin, MessageCircle, Phone } from "lucide-react";
import SEO, { breadcrumbLd, faqLd, serviceLd } from "@/components/SEO";
import Faq from "@/components/Faq";
import { Reveal } from "@/components/Reveal";
import { LocationWhatsAppButton } from "@/components/WhatsAppCTA";
import { CtaBand } from "@/pages/Home";
import { BUSINESS, WA_DEFAULT, waLink } from "@/data/business";
import { SERVICES } from "@/data/services";
import { AREAS } from "@/data/areas";

export default function AreaPage({ area }) {
  const crumb = [
    { name: "الرئيسية", path: "/" },
    { name: "مناطق الخدمة", path: "/#areas" },
    { name: area.name, path: `/${area.slug}` },
  ];
  const otherAreas = AREAS.filter((a) => a.slug !== area.slug);

  return (
    <>
      <SEO
        title={area.title}
        description={area.description}
        path={area.slug}
        image={area.image}
        jsonLd={[
          serviceLd({
            name: `بنشر متنقل في ${area.name}`,
            description: area.description,
            slug: area.slug,
          }),
          faqLd(area.faqs),
          breadcrumbLd(crumb),
        ]}
      />

      <section className="relative overflow-hidden grain pt-28 pb-16">
        <div className="absolute inset-0">
          <img src={area.image} alt={area.alt} fetchPriority="high" className="w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/60" />
          <div className="absolute inset-0 spotlight" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <nav aria-label="مسار التنقل" className="text-xs text-gray-400 mb-5 flex items-center gap-1.5" data-testid="area-breadcrumb">
            <Link to="/" className="hover:text-gold-soft">الرئيسية</Link>
            <ChevronLeft size={12} />
            <Link to="/#areas" className="hover:text-gold-soft">مناطق الخدمة</Link>
            <ChevronLeft size={12} />
            <span className="text-gold-soft">{area.name}</span>
          </nav>
          <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl" data-testid="area-h1">
            {area.h1}
          </h1>
          <p className="mt-6 text-lg text-gray-300 leading-relaxed max-w-2xl">{area.intro[0]}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={waLink(`مرحباً، أرغب في طلب بنشر متنقل في ${area.name}.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="area-order-cta"
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-ink font-black rounded-full px-7 py-3.5 transition-colors"
            >
              <MessageCircle size={18} /> اطلب عبر واتساب — {area.name}
            </a>
            <LocationWhatsAppButton
              testid="area-location-cta"
              label="أرسل موقعك للفني"
              className="inline-flex items-center gap-2 border border-gold/50 text-gold-soft hover:bg-gold hover:text-ink font-bold rounded-full px-7 py-3.5 transition-colors"
            />
            <a
              href={WA_DEFAULT}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="area-whatsapp-cta"
              className="inline-flex items-center gap-2 bg-wa hover:bg-wa-deep text-white font-bold rounded-full px-7 py-3.5 transition-colors"
            >
              <MessageCircle size={18} /> واتساب
            </a>
            <a
              href={BUSINESS.telLink}
              data-testid="area-call-cta"
              className="inline-flex items-center gap-2 border border-blood/60 text-white hover:bg-blood font-bold rounded-full px-7 py-3.5 transition-colors"
            >
              <Phone size={18} /> <span dir="ltr">{BUSINESS.phoneDisplay}</span>
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20">
        <Reveal>
          <p className="text-lg text-gray-300 leading-loose">{area.intro[1]}</p>
        </Reveal>

        <Reveal className="mt-14">
          <h2 className="font-display font-black text-2xl sm:text-3xl text-white mb-6">الأحياء التي نغطيها في {area.name}</h2>
          <div className="flex flex-wrap gap-2.5">
            {area.districts.map((d) => (
              <span key={d} className="inline-flex items-center gap-1.5 bg-panel border border-edge text-gray-200 text-sm rounded-full px-4 py-2">
                <MapPin size={13} className="text-gold" />
                {d}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-4 leading-relaxed">
            وأيضاً الطرق الرئيسية: {area.roads.join("، ")} — وما حولها من شوارع داخلية ومواقف.
          </p>
        </Reveal>

        <Reveal className="mt-14">
          <h2 className="font-display font-black text-2xl sm:text-3xl text-white mb-6">ماذا نقدم في {area.name}؟</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {area.points.map((p, i) => (
              <div key={i} className="flex items-start gap-3 bg-panel border border-edge rounded-xl px-4 py-4 text-sm text-gray-300 leading-relaxed">
                <CheckCircle2 size={17} className="text-gold shrink-0 mt-0.5" />
                {p}
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal className="mt-14">
          <h2 className="font-display font-black text-2xl sm:text-3xl text-white mb-8">أسئلة عن التغطية في {area.name}</h2>
          <Faq items={area.faqs} testidPrefix="area-faq" />
        </Reveal>
      </section>

      <section className="bg-panel border-t border-edge py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h3 className="font-display font-bold text-white mb-5">مناطق أخرى نخدمها في الرياض</h3>
          <div className="flex flex-wrap gap-2.5">
            {otherAreas.map((a) => (
              <Link key={a.slug} to={`/${a.slug}`} data-testid={`area-related-${a.slug}`} className="text-sm border border-edge hover:border-gold/60 hover:text-gold-soft text-gray-300 rounded-full px-4 py-2 transition-colors">
                بنشر متنقل {a.name}
              </Link>
            ))}
          </div>
          <h3 className="font-display font-bold text-white mb-5 mt-10">خدماتنا في هذه المنطقة</h3>
          <div className="flex flex-wrap gap-2.5">
            {SERVICES.slice(0, 6).map((s) => (
              <Link key={s.slug} to={`/${s.slug}`} data-testid={`area-service-${s.slug}`} className="text-sm border border-edge hover:border-gold/60 hover:text-gold-soft text-gray-300 rounded-full px-4 py-2 transition-colors">
                {s.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title={`تعطلت في ${area.name}؟ نحن الطريق إليك`} testid="area-cta-band" />
    </>
  );
}
